import {
  Baseline,
  BookOpen,
  CalendarDays,
  FilePlus2,
  Infinity as Infinito,
  Image as ImageIcon,
  LayoutGrid,
  LayoutPanelTop,
  NotebookPen,
  Palette,
  PenLine,
  Rows3,
  UserRound,
  Wand2,
  type LucideIcon,
} from 'lucide-react';
import { PassoCalendario } from './passos/PassoCalendario';
import { PassoCadernos } from './passos/PassoCadernos';
import { PassoCores } from './passos/PassoCores';
import { PassoDadosPessoais, PassoExtras } from './passos/PassoDadosExtras';
import { PassoEstilo } from './passos/PassoEstilo';
import { PassoDiasPorPagina, PassoFormato, PassoSemana } from './passos/PassoEstrutura';
import { PassoImagens } from './passos/PassoImagens';
import { PassoLinhas } from './passos/PassoLinhas';
import { PassoModelo } from './passos/PassoModelo';
import { PassoQuadros } from './passos/PassoQuadros';
import { PassoRevisao } from './passos/PassoRevisao';
import { PassoTema } from './passos/PassoTema';
import type { ConfigPlanner } from '@miolos/core';

export interface DefinicaoPasso {
  id: string;
  titulo: string;
  resumo: string;
  icone: LucideIcon;
  Componente: () => React.ReactElement;
  /**
   * Passo que só faz sentido em algumas configurações.
   *
   * Ex.: “Dias da semana” desaparece quando o miolo não tem páginas de dia —
   * não há dia nenhum para escolher.
   */
  visivel?: (config: ConfigPlanner) => boolean;
}

/** Chave do passo no fluxo de criação liberado pelo administrador. */
export function chaveDoPasso(id: string): string {
  return `passo:${id}`;
}

/** A ordem aqui é a ordem do assistente — e segue a ordem pedida pelo produto. */
export const PASSOS: DefinicaoPasso[] = [
  {
    id: 'modelo',
    titulo: 'Modelo',
    resumo: 'Comece de um modelo pronto ou do zero',
    icone: Wand2,
    Componente: PassoModelo,
  },
  {
    id: 'formato',
    titulo: 'Tamanho',
    resumo: 'A4, A5 ou A6',
    icone: LayoutGrid,
    Componente: PassoFormato,
  },
  {
    id: 'dias-por-pagina',
    titulo: 'Dias por página',
    resumo: '1 ou 2 dias',
    icone: Rows3,
    Componente: PassoDiasPorPagina,
  },
  {
    id: 'semana',
    titulo: 'Dias da semana',
    resumo: 'Seg–Sex, Seg–Sáb, Seg–Dom ou Sáb–Dom',
    icone: CalendarDays,
    Componente: PassoSemana,
    // Sem páginas de dia não existe dia da semana para escolher.
    visivel: (config) => config.paginasDeDia !== 'NENHUM',
  },
  {
    id: 'cores',
    titulo: 'Cores',
    resumo: 'Teoria das cores aplicada',
    icone: Palette,
    Componente: PassoCores,
  },
  {
    id: 'estilo',
    titulo: 'Conteúdo',
    resumo: 'Devocional, motivacional ou livre',
    icone: BookOpen,
    Componente: PassoEstilo,
  },
  {
    id: 'calendario',
    titulo: 'Período',
    resumo: 'Datada ou permanente',
    icone: Infinito,
    Componente: PassoCalendario,
    /**
     * Sem páginas de dia não há data para escolher — o passo só volta a fazer
     * sentido quando alguma página extra depende de data (visão do ano ou
     * divisores de mês).
     */
    visivel: (config) =>
      config.paginasDeDia !== 'NENHUM' ||
      config.paginasExtras.includes('ANO') ||
      config.paginasExtras.includes('MESES'),
  },
  {
    id: 'tema',
    titulo: 'Tema e estilo',
    resumo: 'Acabamento, tema, ornamento e tipografia',
    icone: Wand2,
    Componente: PassoTema,
  },
  {
    id: 'linhas',
    titulo: 'Linhas e desenhos',
    resumo: 'Traço da pauta e desenhos das linhas',
    icone: PenLine,
    Componente: PassoLinhas,
  },
  {
    id: 'imagem',
    titulo: 'Imagens',
    resumo: 'Camadas, posição e rodízio',
    icone: ImageIcon,
    Componente: PassoImagens,
  },
  {
    id: 'dados-pessoais',
    titulo: 'Meus dados',
    resumo: 'Proprietário e contatos',
    icone: UserRound,
    Componente: PassoDadosPessoais,
  },
  {
    id: 'extras',
    titulo: 'Páginas extras',
    resumo: 'Índice, metas, notas...',
    icone: FilePlus2,
    Componente: PassoExtras,
  },
  {
    id: 'cadernos',
    titulo: 'Cadernos',
    resumo: 'Contatos, senhas, orações, caligrafia, partitura...',
    icone: NotebookPen,
    Componente: PassoCadernos,
  },
  {
    id: 'quadros',
    titulo: 'Quadros do dia',
    resumo: 'O que aparece em cada página',
    icone: LayoutPanelTop,
    Componente: PassoQuadros,
    // Quadro do dia pressupõe página de dia.
    visivel: (config) => config.paginasDeDia !== 'NENHUM',
  },
  {
    id: 'revisao',
    titulo: 'Revisão',
    resumo: 'Resumo e checagem final',
    icone: Baseline,
    Componente: PassoRevisao,
  },
];

/**
 * Passos que fazem sentido agora.
 *
 * Duas peneiras, nesta ordem:
 *  1. o administrador pode ainda não ter liberado o passo no fluxo de criação;
 *  2. o passo pode não se aplicar à configuração (sem páginas de dia, “Dias da
 *     semana” e “Quadros do dia” saem; sem data em jogo, “Período” sai).
 *
 * O “Continuar” então pula direto para o próximo passo aplicável.
 */
export function passosVisiveis(
  config: ConfigPlanner,
  liberado?: (chave: string) => boolean,
): DefinicaoPasso[] {
  return PASSOS.filter((passo) => (liberado ? liberado(chaveDoPasso(passo.id)) : true)).filter(
    (passo) => passo.visivel?.(config) ?? true,
  );
}
